import React from "react";
import { Link } from "react-router-dom";
import { Reveal, SectionLabel, Accordion, renderPh, scrollToId, Ph, SectionBg } from "./fx";
import { HostPanel } from "./HostPanel";
import { RegForm } from "./RegForm";
import { PH } from "./copy";

const CTA = ({ label, testId }) => (
  <button data-testid={testId} data-cta onClick={() => scrollToId("register")} className="btn-accent px-8 py-4 text-sm tracking-widest uppercase">
    {label}
  </button>
);

export const Curriculum = ({ t }) => (
  <section className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" data-testid="curriculum-section">
    <SectionBg img="/img/home4.jpg" />
    <div className="relative max-w-[1000px] mx-auto">
      <Reveal>
        <div className="flex flex-wrap justify-between items-end gap-4 mb-12">
          <div>
            <SectionLabel>{t.curriculum.label}</SectionLabel>
            <h2 className="h1-display text-3xl md:text-5xl">{t.curriculum.h2}</h2>
          </div>
          <div className="mono text-[11px] tracking-[0.2em] text-white/35">{renderPh(t.curriculum.meta)}</div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion items={t.curriculum.modules} testPrefix="curriculum" />
      </Reveal>
    </div>
  </section>
);

export const Host = ({ t }) => (
  <section id="host" className="py-24 md:py-32 px-6 md:px-12" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="host-section">
    <div className="max-w-[1200px] mx-auto">
      <Reveal><SectionLabel>{t.host.label}</SectionLabel></Reveal>
      <div className="grid md:grid-cols-[minmax(0,420px)_1fr] gap-12 md:gap-16 items-start">
        <Reveal><HostPanel /></Reveal>
        <Reveal delay={0.15}>
          <h2 className="h1-display text-3xl md:text-[44px] mb-6">{t.host.h2}</h2>
          <div className="h-px bg-[#4D9FFF] w-24 mb-8" />
          <div className="space-y-5 text-white/55 text-[15px] leading-[1.8] mb-10">
            {t.host.bio.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mb-10" data-testid="host-credential-grid">
            {t.host.creds.map((c, i) => (
              <div key={i} className="bg-[#123059] p-5 lift-card">
                <div className="mono text-[9px] tracking-[0.3em] text-white/30 mb-2">{c.l}</div>
                <div className="text-sm font-bold">{c.ph ? <Ph>{c.v}</Ph> : c.v}</div>
              </div>
            ))}
          </div>
          <CTA label={t.cta} testId="host-cta" />
        </Reveal>
      </div>
    </div>
  </section>
);

export const Empathy = ({ t }) => (
  <section className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" data-testid="empathy-section">
    <SectionBg img="/img/home2.jpg" />
    <div className="relative max-w-[1200px] mx-auto grid md:grid-cols-2 gap-14 md:gap-20">
      <Reveal>
        <SectionLabel>{t.empathy.label}</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-black mb-8 leading-tight">{t.empathy.h2}</h2>
        {t.empathy.pains.map((p, i) => (
          <p key={i} className="text-white/50 text-[15px] leading-[1.85] mb-5">{p}</p>
        ))}
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mono accent text-xs tracking-[0.25em] mb-8">{t.empathy.changesLabel}</div>
        <ul className="space-y-6 mb-10">
          {t.empathy.transforms.map((tr, i) => (
            <li key={i} className="flex gap-4 items-start" data-testid={`transform-bullet-${i + 1}`}>
              <span className="accent font-black text-xl shrink-0">→</span>
              <span className="text-white/75 text-base leading-relaxed">{tr}</span>
            </li>
          ))}
        </ul>
        <CTA label={t.cta} testId="empathy-cta" />
      </Reveal>
    </div>
  </section>
);

export const Takeaways = ({ t }) => (
  <section className="py-24 md:py-32 px-6 md:px-12" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="takeaways-section">
    <div className="max-w-[1200px] mx-auto">
      <Reveal>
        <SectionLabel>{t.takeaways.label}</SectionLabel>
        <h2 className="h1-display text-3xl md:text-5xl mb-14">{t.takeaways.h2}</h2>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {t.takeaways.cards.map((c, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="audience-card p-6 h-full" data-testid={`takeaway-card-${i + 1}`}>
              <div className="text-3xl mb-4">{c.i}</div>
              <div className="font-black uppercase text-sm tracking-wide mb-3">{c.t}</div>
              <p className="text-white/45 text-[13px] leading-relaxed">{c.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Faq = ({ t }) => (
  <section id="faq" className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12" data-testid="faq-section">
    <SectionBg img="/img/home3.jpg" />
    <div className="relative max-w-[900px] mx-auto">
      <Reveal>
        <SectionLabel>{t.faq.label}</SectionLabel>
        <h2 className="h1-display text-3xl md:text-5xl mb-12">{t.faq.h2}</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion items={t.faq.items} testPrefix="faq" />
      </Reveal>
    </div>
  </section>
);

export const FinalRegister = ({ t, lang }) => (
  <section
    id="register"
    className="py-24 md:py-36 px-6 md:px-12 grid-bg relative overflow-hidden"
    data-testid="final-register-section"
  >
    <div
      className="absolute left-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(77,159,255,0.13), transparent 65%)" }}
    />
    <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-14 items-center relative">
      <Reveal>
        <SectionLabel>{t.final.label}</SectionLabel>
        <h2 className="h1-display text-[clamp(36px,4.6vw,60px)] mb-10">
          {t.final.h2a}<span className="accent">{t.final.h2b}</span>
        </h2>
        <ul className="space-y-4">
          {t.final.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 items-start text-white/65 text-[15px]">
              <span className="accent font-black shrink-0">✓</span>{b}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="glass-card p-6 md:p-8">
          <div className="mono accent text-[10px] tracking-[0.35em] mb-4">{t.card.label}</div>
          <RegForm t={t} lang={lang} idPrefix="final" />
        </div>
      </Reveal>
    </div>
  </section>
);

const EqualHousingLogo = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" role="img" aria-label="Equal Housing Opportunity" className="shrink-0">
    <path d="M12 1.6 L23 9.6 H20.4 V22.4 H3.6 V9.6 H1 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <rect x="8" y="12" width="8" height="2.1" fill="currentColor" />
    <rect x="8" y="16.2" width="8" height="2.1" fill="currentColor" />
  </svg>
);

export const Footer = ({ t }) => (
  <footer className="relative pt-28 pb-10 px-6 md:px-12 border-t border-white/10 overflow-hidden" data-testid="footer-section">
    <SectionBg img="/img/home1.jpg" opacity={0.1} />
    <div
      className="absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap font-black uppercase select-none pointer-events-none"
      style={{ fontSize: "14vw", color: "rgba(255,255,255,0.03)", letterSpacing: "-0.03em" }}
    >
      THE STRATEGIC BUYER SESSION
    </div>
    <div className="max-w-[1200px] mx-auto relative">
      <div className="grid md:grid-cols-3 gap-10 mb-14 text-sm text-white/45">
        <div className="space-y-2" data-testid="footer-legal-col">
          <div className="font-bold text-white">Plinio Angulo | NMLS #1443167</div>
          <div>Guaranteed Rate Inc. DBA Rate | Company NMLS #2611</div>
          <div className="text-xs">Licenciado en NY, NJ, CT, PA y FL</div>
          <div className="flex items-center gap-3 pt-2 text-white/60" data-testid="footer-equal-housing">
            <EqualHousingLogo />
            <span className="text-xs leading-snug">Igualdad de Oportunidades<br />en la Vivienda</span>
          </div>
        </div>
        <div className="space-y-2">
          {t.footer.contact.map((c, i) => <div key={i} className="text-xs">{renderPh(c)}</div>)}
        </div>
        <div className="space-y-2">
          {t.footer.legalLinks.map((l, i) => (
            <div key={i}><Link data-testid={`footer-legal-link-${i}`} to={l.to} className="text-xs hover:text-[#4D9FFF] transition-colors underline underline-offset-4 decoration-white/20">{l.t}</Link></div>
          ))}
        </div>
      </div>
      <div className="border border-white/10 p-6 mb-10" data-testid="footer-compliance-block">
        <div className="flex items-start gap-3">
          <span className="text-xl" title="Equal Housing Opportunity">⌂</span>
          <div className="text-xs text-white/35 leading-relaxed space-y-2">
            <div>{renderPh(t.footer.compliance)}</div>
            <div className="italic opacity-70">{t.footer.complianceDraft}</div>
          </div>
        </div>
      </div>
      <div className="mono text-[10px] text-white/20 tracking-[0.15em]" data-testid="footer-copyright">
        {renderPh(t.footer.copyright)}
      </div>
    </div>
  </footer>
);

export const MobileCTA = ({ t }) => (
  <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden glass-card p-3 border-t border-white/10" data-testid="mobile-sticky-cta">
    <button data-cta onClick={() => scrollToId("register")} className="btn-accent w-full py-4 text-sm tracking-widest uppercase">
      {t.cta}
    </button>
  </div>
);
